import { useTranslation } from "react-i18next";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
} from "@nexus/ui";
import type { VertexUsage } from "@/features/llm-usage/api/llm-usage.api";
import { formatTokensCompact } from "../lib/format-tokens";
import {
  summarizeVertexReconciliation,
  type RecordedLlmUsageForVertex,
  type VertexReconciliationStatus,
} from "../lib/vertex-reconciliation";
import { VertexReconciliationPanel } from "./VertexReconciliationPanel";

interface VertexReconciliationDialogProps {
  data: VertexUsage | undefined;
  isLoading: boolean;
  recorded: RecordedLlmUsageForVertex;
  /** `tokenUsageLongTailPercent`, passed through to the panel's fold. */
  longTailPercent: number;
}

const STATUS_ICON: Record<VertexReconciliationStatus, "info" | "check" | "alert-circle"> = {
  unavailable: "info",
  reconciled: "check",
  gap: "alert-circle",
  shortfall: "alert-circle",
};

/**
 * The reconciliation, folded into the filter row.
 *
 * It answers one question — does the breakdown account for the whole bill —
 * and the answer is a single sentence, so the sentence goes on the button and
 * the models behind it stay out of the page's height. Nothing is hidden that
 * an operator has to act on: a gap still says so before it is opened.
 */
export function VertexReconciliationDialog({
  data,
  isLoading,
  recorded,
  longTailPercent,
}: VertexReconciliationDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const summary = summarizeVertexReconciliation(data, recorded);
  const tokens = formatTokensCompact(summary.gapTokens, locale);

  const label =
    summary.status === "unavailable"
      ? t("admin.llmUsage.vertex.unreadable", "Vertex not readable")
      : summary.status === "reconciled"
        ? t("admin.llmUsage.vertex.reconciled", "Matches what Nexus recorded")
        : summary.status === "gap"
          ? t("admin.llmUsage.vertex.gap", "{{tokens}} not attributed to a Nexus call", { tokens })
          : t(
              "admin.llmUsage.vertex.shortfall",
              "{{tokens}} recorded by Nexus that Vertex has not reported",
              { tokens },
            );

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          // Colour is the secondary signal here; the sentence itself already
          // says which way the numbers disagree.
          className={cn("ml-auto text-xs", summary.status === "gap" && "text-warning")}
          disabled={isLoading && !data}
        >
          <Icon
            name={STATUS_ICON[summary.status]}
            size="xs"
            colorVar={
              summary.status === "reconciled"
                ? "--success"
                : summary.status === "unavailable"
                  ? undefined
                  : "--warning"
            }
            aria-hidden
          />
          {label}
        </Button>
      </DialogTrigger>

      <DialogContent variant="form">
        <DialogHeader>
          <DialogTitle>{t("admin.llmUsage.vertex.title", "Reported by Vertex")}</DialogTitle>
          <DialogDescription>
            {t(
              "admin.llmUsage.vertex.dialogDescription",
              "Google counts every call this Google Cloud project makes. Nexus counts only its own. This compares the two.",
            )}
          </DialogDescription>
        </DialogHeader>

        <VertexReconciliationPanel
          data={data}
          isLoading={isLoading}
          recorded={recorded}
          longTailPercent={longTailPercent}
        />
      </DialogContent>
    </Dialog>
  );
}
